import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import { LearningWorkspace, TrainingFilters } from './LearningWorkspace';
// pages/training/MyTrainingPage.tsx
import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Chip,
  Input,
  Stack,
  Sheet,
  Tabs,
  TabList,
  Tab,
  Alert,
  LinearProgress,
  CircularProgress,
} from '@mui/joy';
import {
  MagnifyingGlass,
  Warning,
  ArrowRight,
  Clock,
  CheckCircle,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { PageHelpButton } from '../../components/knowledge-base/PageHelpButton';
import { useMyTraining, useMyTrainingSummary, isUnlinkedLoginError } from '../../hooks/useTraining';
import type { MyTrainingRow } from '../../types/training';
import {
  contentTypeIcons,
  contentTypeLabels,
  credentialTypeLabels,
  getActionLabel,
  getDeadlineChip,
} from './constants';

type TrainingTab = 'todo' | 'completed' | 'all';

const isDone = (row: MyTrainingRow) => row.status === 'completed';

/** One assigned item, sized for a phone held in a truck. */
const TrainingCard: React.FC<{ row: MyTrainingRow; onOpen: () => void }> = ({ row, onOpen }) => {
  const deadline = getDeadlineChip(row);
  const credential = credentialTypeLabels[row.credential_type];

  return (
    <Card
      variant="outlined"
      onClick={onOpen}
      sx={{ p: { xs: 1.5, md: 2 }, gap: 1, cursor: 'pointer', '&:hover': { borderColor: 'primary.400' } }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
        <Sheet
          variant="soft"
          color="primary"
          sx={{
            borderRadius: 'sm',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {contentTypeIcons[row.content_type]}
        </Sheet>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography level="title-md">{row.title}</Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            {deadline && (
              <Chip size="sm" variant="soft" color={deadline.color}>
                {deadline.label}
              </Chip>
            )}
            {row.is_mandatory && (
              <Chip size="sm" variant="soft" color="danger">
                Required
              </Chip>
            )}
            {credential && (
              <Chip size="sm" variant="outlined" color="neutral">
                {credential}
              </Chip>
            )}
            <Chip size="sm" variant="outlined" color="neutral">
              {contentTypeLabels[row.content_type]}
            </Chip>
            {row.estimated_duration_minutes > 0 && (
              <Chip size="sm" variant="outlined" color="neutral" startDecorator={<Clock />}>
                {row.estimated_duration_minutes} min
              </Chip>
            )}
          </Stack>
          {row.status === 'in_progress' && row.watch_percentage > 0 && (
            <LinearProgress
              determinate
              value={row.watch_percentage}
              sx={{ mt: 1.25 }}
              aria-label={`${row.watch_percentage}% watched`}
            />
          )}
          {row.status === 'failed' && typeof row.score === 'number' && (
            <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>
              Scored {row.score}% — not a pass yet.
            </Typography>
          )}
        </Box>
      </Stack>
      <Button
        variant={isDone(row) ? 'outlined' : 'solid'}
        color={isDone(row) ? 'neutral' : 'primary'}
        endDecorator={<ArrowRight />}
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
        sx={{ alignSelf: { xs: 'stretch', sm: 'flex-end' } }}
      >
        {getActionLabel(row)}
      </Button>
    </Card>
  );
};

const SummaryTile: React.FC<{
  label: string;
  value: number | string;
  color?: 'danger' | 'warning' | 'success' | 'neutral';
}> = ({ label, value, color = 'neutral' }) => (
  <Sheet variant="soft" color={color} sx={{ borderRadius: 'sm', p: { xs: 1.25, md: 1.5 }, minWidth: 0 }}>
    <Typography level="h3" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
      {value}
    </Typography>
    <Typography level="body-xs">{label}</Typography>
  </Sheet>
);

export const MyTrainingPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TrainingTab>('todo');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ due: '', requirement: '', contentType: '', ordering: 'due_date' });

  const { data, isLoading, error } = useMyTraining({ ordering: filters.ordering, page_size: 20, page, view: tab, search, due: filters.due, content_type: filters.contentType, is_mandatory: filters.requirement ? filters.requirement === 'true' : undefined });
  const { data: summary } = useMyTrainingSummary();

  const rows = useMemo(() => data?.results ?? [], [data]);

  const openItem = (row: MyTrainingRow) => navigate(`/training/${row.id}`);

  // What to do next: the most overdue thing, else the soonest due thing.
  const nextUp = useMemo(() => {
    const outstanding = rows.filter((row) => !isDone(row));
    return outstanding.find((row) => row.is_overdue) ?? outstanding[0] ?? null;
  }, [rows]);

  const visibleRows = rows;
  const completedCount = summary?.completed || 0;
  const todoCount = (summary?.total || 0) - completedCount;

  if (error) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined }, mb: 2 }}>
          My Training
        </Typography>
        <Alert color={isUnlinkedLoginError(error) ? 'warning' : 'danger'} startDecorator={<Warning />}>
          {isUnlinkedLoginError(error)
            ? 'Your login is not linked to a team member record yet, so there is no training to show. Ask HR to link it.'
            : 'We could not load your training. Please try again in a moment.'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>
            My Training
          </Typography>
          <PageHelpButton />
        </Box>
        <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
          Your assigned training, what is due, and what has expired.
        </Typography>
      </Box>

      {summary && summary.total > 0 && (
        <Card variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
          {/* Grid, not a row: five tiles must not push the page sideways on a phone. */}
          <Box
            sx={{
              display: 'grid',
              gap: 1,
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fit, minmax(120px, 1fr))' },
            }}
          >
            <SummaryTile label="Assigned" value={summary.total} />
            <SummaryTile label="Not started" value={summary.not_started} />
            <SummaryTile label="In progress" value={summary.in_progress} color="warning" />
            <SummaryTile label="Completed" value={summary.completed} color="success" />
            {summary.overdue > 0 && <SummaryTile label="Overdue" value={summary.overdue} color="danger" />}
          </Box>
          <Box>
            <Typography level="body-xs" sx={{ mb: 0.5 }}>
              {summary.completion_rate}% complete
            </Typography>
            <LinearProgress
              determinate
              value={summary.completion_rate}
              color={summary.overdue > 0 ? 'warning' : 'success'}
              aria-label="Overall completion"
            />
          </Box>
        </Card>
      )}

      <LearningWorkspace />
      {nextUp && (
        <Card
          variant="soft"
          color={nextUp.is_overdue ? 'danger' : 'primary'}
          onClick={() => openItem(nextUp)}
          sx={{ p: { xs: 1.5, md: 2 }, mb: 2, cursor: 'pointer' }}
        >
          <Typography level="body-xs" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {nextUp.is_overdue ? 'Overdue — do this first' : 'Next up'}
          </Typography>
          <Typography level="title-lg">{nextUp.title}</Typography>
          <Button
            endDecorator={<ArrowRight />}
            color={nextUp.is_overdue ? 'danger' : 'primary'}
            onClick={(event) => {
              event.stopPropagation();
              openItem(nextUp);
            }}
            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          >
            {getActionLabel(nextUp)}
          </Button>
        </Card>
      )}

      <Tabs
        value={tab}
        onChange={(_, value) => { setTab(value as TrainingTab); setPage(1); }}
        sx={{ bgcolor: 'transparent', mb: 2 }}
      >
        <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 30%', sm: '0 0 auto' } } }}>
          <Tab value="todo">To do ({todoCount})</Tab>
          <Tab value="completed">Completed ({completedCount})</Tab>
          <Tab value="all">All ({summary?.total || 0})</Tab>
        </TabList>
      </Tabs>

<CollapsibleFilters>      <Input
          placeholder="Search my training"
          startDecorator={<MagnifyingGlass />}
          value={search}
          onChange={(event) => { setSearch(event.target.value); setPage(1); }}
          sx={{ mb: 2 }}
        />

      <TrainingFilters {...filters} onChange={(key, value) => { setFilters({ ...filters, [key]: value }); setPage(1); }} />
</CollapsibleFilters>      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : visibleRows.length === 0 ? (
        <Card variant="soft" sx={{ textAlign: 'center', py: 4, gap: 1 }}>
          {summary?.total === 0 ? (
            <>
              <Typography level="body-lg">No training assigned to you yet</Typography>
              <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                Anything HR assigns — OSHA, fall protection, ladder safety — shows up here.
              </Typography>
            </>
          ) : tab === 'todo' && !search && !filters.due && !filters.requirement && !filters.contentType && todoCount === 0 ? (
            <>
              <CheckCircle size={32} />
              <Typography level="body-lg">You are all caught up</Typography>
            </>
          ) : (
            <Typography level="body-lg">No training matches these filters</Typography>
          )}
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {visibleRows.map((row) => (
            <TrainingCard key={row.id} row={row} onOpen={() => openItem(row)} />
          ))}
        </Stack>
      )}
      <Stack direction="row" sx={{ gap: 1, mt: 2, alignItems: 'center' }}><Button disabled={!data?.previous || isLoading} onClick={() => setPage(page - 1)}>Previous</Button><Typography>Page {page} · {data?.count || 0} matching assignments</Typography><Button disabled={!data?.next || isLoading} onClick={() => setPage(page + 1)}>Next</Button></Stack>
    </Box>
  );
};

export default MyTrainingPage;
